/* CONTEXT:START
Component: Joe Configuration API Service
Status: implementation
Dependencies: Real backend API endpoints, AWS Cognito authentication
Next: Integration with frontend components
CONTEXT:END */

import { fetchAuthSession } from 'aws-amplify/auth';

// Joe Configuration interfaces (matching backend types)
export interface JoeConfiguration {
  id: string;                    // Natural key: "joe-config-v{version}"
  systemPrompt: string;          // 50-3500 characters
  userPromptTemplate: string;    // 50-2500 characters
  version: number;               // Version number for optimistic locking
  isActive: boolean;             // Only one active config at a time
  createdAt: string;             // ISO timestamp
  createdBy: string;             // User ID who created this config
  createdByName?: string;        // Display name of user who created this config
  description?: string;          // Optional description of changes
}

export interface JoeConfigurationHistory {
  id: string;                    // Natural key: "joe-history-{timestamp}-{action}"
  configurationId: string;       // Reference to configuration
  action: 'CREATED' | 'ACTIVATED' | 'REVERTED';
  performedBy: string;           // User ID
  performedAt: string;           // ISO timestamp
  previousConfigId?: string;     // For reversion tracking
  notes?: string;                // Optional notes
}

export interface JoeTestRequest {
  systemPrompt: string;
  userPromptTemplate: string;
  testContent: string;
  entryType: 'highlight' | 'lowlight';
  month: number;
  year: number;
}

export interface JoeTestResponse {
  feedback: string;
  processingTimeMs: number;
  tokenUsage?: number;
  success: boolean;
  error?: string;
}

// API Response wrapper interface
interface ApiResponse<T> {
  success: boolean;
  data: T;
  timestamp: string;
  requestId: string;
}

interface ApiErrorResponse {
  error: {
    code: string;
    message: string;
    details?: Record<string, string>;
  };
  timestamp: string;
  requestId: string;
}

export class JoeConfigurationApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = process.env.REACT_APP_API_URL || '';
    if (!this.baseUrl) {
      console.warn('REACT_APP_API_URL not configured. Joe Configuration API will not work.');
    }
  }

  /**
   * Get authentication headers for API requests
   */
  private async getAuthHeaders(): Promise<Record<string, string>> {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (!token) {
        throw new Error('No authentication token available');
      }
      
      return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
    } catch (error) {
      console.error('Failed to get authentication token:', error);
      throw new Error('Authentication required. Please log in.');
    }
  }

  /**
   * Handle API response and extract data
   */
  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      
      try {
        const errorData: ApiErrorResponse = await response.json();
        errorMessage = errorData.error.message || errorMessage;
      } catch {
        // If we can't parse error response, use the default message
      }
      
      throw new Error(errorMessage);
    }

    const responseData: ApiResponse<T> = await response.json();
    
    if (!responseData.success) {
      throw new Error('API request failed');
    }

    return responseData.data;
  }

  /**
   * Get the currently active Joe configuration
   */
  async getActiveConfiguration(): Promise<JoeConfiguration> {
    const headers = await this.getAuthHeaders();
    
    const response = await fetch(`${this.baseUrl}/api/admin/joe/configuration`, {
      method: 'GET',
      headers
    });

    return this.handleResponse<JoeConfiguration>(response);
  }

  /**
   * Save a new Joe configuration
   */
  async saveConfiguration(
    config: Omit<JoeConfiguration, 'id' | 'createdAt' | 'version'>
  ): Promise<JoeConfiguration> {
    const headers = await this.getAuthHeaders();
    
    const response = await fetch(`${this.baseUrl}/api/admin/joe/configuration`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(config)
    });

    return this.handleResponse<JoeConfiguration>(response);
  }

  /**
   * Get Joe configuration history with optional limit
   */
  async getConfigurationHistory(limit: number = 10): Promise<JoeConfiguration[]> {
    const headers = await this.getAuthHeaders();
    
    const url = new URL(`${this.baseUrl}/api/admin/joe/history`);
    url.searchParams.append('limit', limit.toString());
    
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers
    });

    const responseData = await this.handleResponse<{
      configurations: JoeConfiguration[];
      count: number;
      limit: number;
    }>(response);

    return responseData.configurations;
  }

  /**
   * Test a Joe configuration with sample content
   */
  async testConfiguration(request: JoeTestRequest): Promise<JoeTestResponse> {
    const headers = await this.getAuthHeaders();
    
    const response = await fetch(`${this.baseUrl}/api/admin/joe/test`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request)
    });

    return this.handleResponse<JoeTestResponse>(response);
  }

  /**
   * Revert to a previous Joe configuration
   */
  async revertConfiguration(configurationId: string): Promise<void> {
    const headers = await this.getAuthHeaders();
    
    const response = await fetch(`${this.baseUrl}/api/admin/joe/revert`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ configurationId })
    });

    await this.handleResponse<{
      message: string;
      configurationId: string;
      revertedBy: string;
      revertedAt: string;
    }>(response);
  }

  /**
   * Check if the API service is properly configured
   */
  isConfigured(): boolean {
    return !!this.baseUrl;
  }

  /**
   * Get the API base URL (for debugging)
   */
  getBaseUrl(): string {
    return this.baseUrl;
  }
}

// Export singleton instance for use in components
export const joeConfigurationApiService = new JoeConfigurationApiService();

// Export both services for easy switching during development
export { MockJoeConfigurationService, mockJoeConfigService } from './mockJoeData';

// Default export is the real API service
export default joeConfigurationApiService;
